import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, generateQueryString, get, getBlob, post, put, unwrapItems } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';

import {
	IPagedResponse,
	ISurvey,
	ISurveyCreatePayload,
	ISurveyExportFile,
	ISurveyQuestionType,
	ISurveysQuery,
	ISurveyStatus,
	ISurveyUpdatePayload,
	TSurveyExportFormat
} from './types';

// GET api/surveys — listă paginată cu filtre (Search / ProjectId / StatusId) + sortare.
async function fetchSurveysList(
	query: ISurveysQuery,
	page: number,
	items: number,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<ISurvey[]> {
	try {
		const queryString = generateQueryString({
			...query,
			PageNumber: page + 1,
			PageSize: items
		});
		const res = await get(`api/surveys${queryString}`);
		const data = res.data as IPagedResponse<ISurvey>;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return data.items || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorListLoad'));
	}
}

// GET api/surveys/:id → detaliul unei consultări (întrebări cu agregate + răspunsuri).
async function fetchSingleSurvey(surveyId: number | string): Promise<ISurvey> {
	try {
		const res = await get(`api/surveys/${surveyId}`);
		return res.data as ISurvey;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorNotFound'));
	}
}

// POST api/surveys (SurveyCreateDto) — răspunde cu survey-ul creat (aceeași formă ca detaliul).
async function postSurvey(payload: ISurveyCreatePayload): Promise<ISurvey> {
	try {
		const res = await post('api/surveys', { ...payload });
		return res.data as ISurvey;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorCreate'));
	}
}

// PUT api/surveys/:id (SurveyUpdateDto) — orice 2xx = succes. Statusul NU se schimbă
// aici (publish/archive au endpointuri dedicate) și nici proiectul (nu e în DTO).
async function putSurvey(
	surveyId: number | string,
	payload: ISurveyUpdatePayload
): Promise<void> {
	try {
		await put(`api/surveys/${surveyId}`, { ...payload });
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorUpdate'));
	}
}

// POST api/surveys/:id/publish — doar pentru ciorne; orice 2xx = succes.
async function postPublishSurvey(surveyId: number | string): Promise<void> {
	try {
		await post(`api/surveys/${surveyId}/publish`);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorPublish'));
	}
}

// POST api/surveys/:id/archive — doar pentru consultările publicate.
async function postArchiveSurvey(surveyId: number | string): Promise<void> {
	try {
		await post(`api/surveys/${surveyId}/archive`);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorArchive'));
	}
}

// DELETE api/surveys/:id
async function deleteSurvey(surveyId: number | string): Promise<void> {
	try {
		await del(`api/surveys/${surveyId}`);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorDelete'));
	}
}

// GET api/surveys/:id/csv | /pdf — exportul generat de BE, descărcat ca blob. Numele
// fișierului vine din Content-Disposition (dacă BE îl setează); altfel îl decide view-ul.
async function fetchSurveyExport(
	surveyId: number | string,
	format: TSurveyExportFormat
): Promise<ISurveyExportFile> {
	try {
		const res = await getBlob(`api/surveys/${surveyId}/export/${format}`);
		const disposition = String(res.headers?.['content-disposition'] ?? '');
		const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition);
		return {
			blob: res.data,
			fileName: match ? decodeURIComponent(match[1]) : null
		};
	} catch {
		// La responseType blob, corpul erorii e tot un Blob — nu putem extrage mesajul BE.
		throw new Error(
			format === 'csv'
				? i18n.t('consultations.errorExportCsv')
				: i18n.t('consultations.errorExportPdf')
		);
	}
}

// GET api/survey-statuses — nomenclator mic, îl cerem integral.
async function fetchSurveyStatuses(): Promise<ISurveyStatus[]> {
	try {
		const res = await get('api/survey-statuses?PageNumber=1&PageSize=500');
		return unwrapItems(res.data as ISurveyStatus[] | IPagedResponse<ISurveyStatus>);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorStatusesLoad'));
	}
}

// GET api/survey-question-types — nomenclatorul tipurilor de întrebare.
async function fetchSurveyQuestionTypes(): Promise<ISurveyQuestionType[]> {
	try {
		const res = await get('api/survey-question-types?PageNumber=1&PageSize=500');
		return unwrapItems(
			res.data as ISurveyQuestionType[] | IPagedResponse<ISurveyQuestionType>
		);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorQuestionTypesLoad'));
	}
}

// GET api/surveys/table-config — configurația coloanelor tabelului (chei, etichete,
// ordine, vizibilitate/sortabilitate implicite). Se corelează în pagină cu definițiile
// locale prin applyServerColumnConfig.
async function fetchSurveysTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/surveys/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('consultations.errorTableConfig'));
	}
}

export {
	fetchSurveysTableConfig,
	deleteSurvey,
	fetchSingleSurvey,
	fetchSurveyExport,
	fetchSurveyQuestionTypes,
	fetchSurveysList,
	fetchSurveyStatuses,
	postArchiveSurvey,
	postPublishSurvey,
	postSurvey,
	putSurvey
};
